// Popup „Zuweisen / neuen Kontakt erstellen" (Plan 01.10.2026, B2) — ohne
// Fluent, im kvp-rd-Stil (`.kvp-rd-scrim` + `.dm`), Nachfolger des
// AssignDialog. Übersicht, Akte und Reiter Dokumente nutzen denselben Flow.
//
// Zuweisen heißt seit 01.10.2026 „an den Unternehmensverbund zuweisen": Das
// Backend weitet die Zuweisung an eine Firma auf Tochter-, Schwester- und
// Beteiligungsfirmen aus (`KundenaktenFolderService.assign`). Hier wird EIN
// Kontakt gewählt; der Server bucht die Dokumente aus der Ablage binnen zwei
// Minuten, es wird nichts mehr vom Share importiert.
//
// Findet die Suche niemanden, legt das Popup den Kontakt selbst an: Status
// zuerst, dann Firma | Privat, Name aus dem Suchtext, dazu die Folgefelder der
// Rollen („+ Person mit Beziehung", „+ Unternehmen"/„+ Verein"), die nach dem
// Anlegen gespeichert werden. „weitere Infos" wechselt in die vollständige
// Anlage und kommt mit zugewiesener Akte zurück (`kundenakteRuecksprung`).

import * as React from "react";
import { useEffect, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { useLocation, useNavigate } from "react-router";
import { apiUrl } from "../../utils/api";
import { KundePicker, KundeAuswahl, kontaktName } from "../calendar/KundePicker";
import { Folder, alleKontaktNamen, contactName } from "./types";
import { NeuerKontaktAnlage, listeAusFeld, type AnlageFelder } from "./NeuerKontaktAnlage";
import { kontaktPayload, namenAusSuchtext } from "./kontaktAusSuchtext";
import type { KontaktTyp } from "../forms/KontaktStatusSchritt";
import { rollenAusFeld, statusFuerApi } from "../../data/kontaktstatusSegment";
import { ownershipAusFormular, ownershipPayload, persistCollectedRelations } from "../relationsInline/persistRelations";
import type { HouseholdItem, PersonFirmaItem } from "../relationsInline/types";
import { extractCreatedContactId, ownershipSet } from "../mail/mailContactAssign";
import { anlagePfadAusSuche } from "../../utils/nameAusSuche";
import { mitRuecksprung } from "../../utils/kundenakteRuecksprung";
import { Z_LAYERS } from "../windowManager/zLayers";
import { kundenaktenVorgangService, zuEntscheiden, type KundenaktenVorgangEintrag } from "../../services/kundenaktenVorgangService";
import { VorgaengeEntscheiden } from "./VorgaengeEntscheiden";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

export const ZuweisenPopup: React.FC<{
  folder: Folder;
  onClose: () => void;
  onAssigned: (updated: Folder) => void;
}> = ({ folder, onClose, onAssigned }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [kunde, setKunde] = useState<KundeAuswahl | null>(() =>
    folder.contact?.id ? { id: folder.contact.id, name: contactName(folder.contact) } : null,
  );
  const [candidates, setCandidates] = useState<KundeAuswahl[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Zweiter Schritt nach der Zuweisung (C2): Vorgangs-Ordner, die der
  // Automatismus nicht deuten konnte. `zugewiesen` ist der Ordner nach dem
  // Zuweisen, den `onAssigned` am Ende bekommt.
  const [vorgaenge, setVorgaenge] = useState<KundenaktenVorgangEintrag[] | null>(null);
  const [zugewiesen, setZugewiesen] = useState<Folder | null>(null);

  // Anlage-Modus: `null` = bestehenden Kontakt suchen.
  const [anlage, setAnlage] = useState<AnlageFelder | null>(null);
  const [typ, setTyp] = useState<KontaktTyp | null>(null);
  // Schon angelegt, aber die Zuweisung scheiterte: beim nächsten Versuch nicht
  // noch einmal anlegen.
  const [angelegt, setAngelegt] = useState<KundeAuswahl | null>(null);
  const setFeld = React.useCallback(
    (k: string, v: string) => setAnlage((alt) => (alt ? { ...alt, [k]: v } : alt)),
    [],
  );
  const anlageStarten = (suchtext: string) => {
    // Ohne Suchtext: der Ordnername der alten Akte ist meist der Kundenname.
    const n = namenAusSuchtext(suchtext || folder.folderName || "");
    setAnlage({ kontaktStatus: "Interessent", vorname: n.vorname, nachname: n.nachname, firmenname: n.firmenname });
    setTyp(null);
    setError(null);
  };

  // Kandidaten des Sync-Re-Matches (candidateContactIds, CSV) namentlich auflösen.
  useEffect(() => {
    const ids = String(folder.candidateContactIds ?? "")
      .split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => Number.isInteger(n) && n > 0).slice(0, 6);
    if (!ids.length) return;
    const ctrl = new AbortController();
    Promise.all(
      ids.map((id) =>
        fetch(apiUrl(`contact/${id}`), { signal: ctrl.signal })
          .then((r) => (r.ok ? r.json() : null))
          .then((c) => (c ? { id, name: kontaktName(c) } : null))
          .catch(() => null),
      ),
    ).then((list) => setCandidates(list.filter(Boolean) as KundeAuswahl[]));
    return () => ctrl.abort();
  }, [folder.candidateContactIds]);

  // ESC schließt, solange nichts läuft.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    alleFenster.addEventListener("keydown", onKey);
    return () => alleFenster.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const bisher = alleKontaktNamen(folder);
  const own = anlage ? ownershipAusFormular(anlage) : {};
  const anlageFehlt: string | null = !anlage
    ? null
    : !typ
      ? "Bitte Firma oder Privat wählen."
      : typ === "person" && !anlage.nachname?.trim()
        ? "Bitte einen Nachnamen eingeben."
        : typ === "firma" && !anlage.firmenname?.trim()
          ? "Bitte einen Firmennamen eingeben."
          : typ === "firma" && !anlage.rechtsform?.trim()
            ? "Bitte eine Rechtsform wählen."
            : !ownershipSet(own)
              ? "Bitte FO oder BO festlegen."
              : null;

  /** Legt den Kontakt an (einmal), speichert die Folgefelder, liefert ihn für die Zuweisung. */
  const kontaktAnlegen = async (): Promise<KundeAuswahl> => {
    if (angelegt) return angelegt;
    if (!anlage || !typ) throw new Error("Anlage unvollständig.");
    const typen = await fetch(apiUrl("contacttype"))
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => []);
    const typeId =
      (Array.isArray(typen) ? typen : []).find(
        (t: { type?: string }) => String(t?.type).toLowerCase() === (typ === "firma" ? "firma" : "person"),
      )?.id ?? null;
    const { statusName, weitereStatus } = statusFuerApi({
      kontaktStatus: anlage.kontaktStatus ?? "",
      rollen: rollenAusFeld(anlage.kontaktStatusRollen ?? ""),
      auchKundeOderInteressent: anlage.kontaktStatusAuch ?? "",
    });
    const body = kontaktPayload({
      typ,
      vorname: anlage.vorname ?? "",
      nachname: anlage.nachname ?? "",
      firmenname: anlage.firmenname ?? "",
      rechtsform: { legalForm: anlage.rechtsform ?? "", id: anlage.rechtsformId },
      statusName,
      weitereStatus,
      typeId,
      ownership: ownershipPayload(own),
    });
    const res = await fetch(apiUrl("contact"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`Kontakt konnte nicht angelegt werden (HTTP ${res.status})${txt ? `: ${txt.slice(0, 200)}` : ""}.`);
    }
    const id = extractCreatedContactId(await res.json().catch(() => null));
    if (!id) throw new Error("Kontakt angelegt, aber ohne Id zurückgemeldet — bitte über die Suche wählen.");
    const neu = { id, name: String(body.displayName ?? "") };
    setAngelegt(neu);
    await folgefelderSpeichern(id);
    return neu;
  };

  /**
   * Folgefelder der Rollen (Haushalt, weitere Personen, Unternehmen/Verein) am
   * neuen Kontakt speichern — nach der Schnellanlage wie nach der Vollanlage.
   */
  const folgefelderSpeichern = async (id: number): Promise<void> => {
    if (!anlage) return;
    // Folgefelder der Rollen (Haushalt, weitere Personen, Unternehmen/Verein)
    // wie in der Schnellanlage der Kontaktseite. Ein Fehlschlag hier macht die
    // Anlage nicht rückgängig — der Kontakt steht, die Beziehung lässt sich in
    // der Akte nachtragen; die Meldung sagt das.
    const household = listeAusFeld<HouseholdItem>(anlage.rel_household).filter((it) => it.ref);
    const weitere = listeAusFeld<HouseholdItem>(anlage.rel_weitere).filter((it) => it.ref);
    const personFirma = listeAusFeld<PersonFirmaItem>(anlage.rel_personFirma).filter((it) => it.ref);
    if (household.length || weitere.length || personFirma.length) {
      const failures: string[] = [];
      try {
        await persistCollectedRelations(id, { household, weitere, personFirma }, own, { failures });
      } catch (e) {
        failures.push(e instanceof Error ? e.message : String(e));
      }
      if (failures.length) setError(`Kontakt angelegt; nicht gespeichert: ${failures.join(" · ")} — bitte in der Akte nachtragen.`);
    }
  };

  /**
   * Zuweisen. `vollAngelegt`: Der Kontakt kommt aus der Vollanlage („+ Weitere
   * Informationen erfassen") — er steht schon, hier folgen nur noch die
   * Folgefelder und die Zuweisung.
   */
  const submit = async (vollAngelegt?: KundeAuswahl) => {
    if (busy) return;
    if (!vollAngelegt && (anlage ? !!anlageFehlt : !kunde)) return;
    setBusy(true);
    setError(null);
    try {
      let ziel: KundeAuswahl;
      if (vollAngelegt) {
        // Sofort merken: Scheitert die Zuweisung, legt der nächste Versuch
        // den Kontakt nicht noch einmal an.
        setAngelegt(vollAngelegt);
        await folgefelderSpeichern(vollAngelegt.id);
        ziel = vollAngelegt;
      } else {
        ziel = anlage ? await kontaktAnlegen() : kunde!;
      }
      const res = await fetch(apiUrl(`kundenakten-folders/${folder.id}/assign`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contactIds: [ziel.id] }),
      });
      if (!res.ok) {
        const vorab = angelegt || anlage || vollAngelegt ? `Kontakt „${ziel.name}" ist angelegt, die Zuweisung scheiterte: ` : "";
        const msg = vorab + (res.status === 403
          ? "Keine Schreibrechte auf diesen Kontakt."
          : res.status === 404 ? "Kontakt nicht gefunden (Mandant/gelöscht?)." : `Fehler (HTTP ${res.status}).`);
        throw new Error(msg);
      }
      const aktualisiert: Folder = await res.json();
      // Nachfrage zu Vorgangs-Ordnern (C2): nur, wenn etwas nicht deutbar war.
      // Scheitert die Abfrage, bleibt die Nachfrage in der Akte — die Zuweisung
      // ist trotzdem durch.
      let offen: KundenaktenVorgangEintrag[] = [];
      try {
        offen = zuEntscheiden((await kundenaktenVorgangService.liste(folder.id)).eintraege);
      } catch {
        offen = [];
      }
      if (offen.length) {
        setZugewiesen(aktualisiert);
        setVorgaenge(offen);
        return;
      }
      onAssigned(aktualisiert);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Zuweisung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const vorgaengeFertig = () => {
    if (zugewiesen) onAssigned(zugewiesen);
    onClose();
  };

  /** In die vollständige Anlage, mit Rücksprung hierher und Zuweisung nach dem Speichern. */
  const vollstaendigeAnlage = () => {
    const name = typ === "firma" ? anlage?.firmenname ?? "" : [anlage?.nachname, anlage?.vorname].filter(Boolean).join(", ");
    const pfad = anlagePfadAusSuche(typ === "firma" ? "firma" : "privat", name || folder.folderName);
    navigate(mitRuecksprung(pfad, `${location.pathname}${location.search}`, folder.id));
  };

  const abschicken = anlage ? !anlageFehlt : !!kunde;

  return createPortal(
    <div className="kvp-rd kvp-rd-scrim" style={{ zIndex: Z_LAYERS.windowConfirm }} data-testid="zuweisen-scrim">
      <div
        className="dm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="zuweisen-titel"
        style={{ width: anlage ? "min(760px, 100%)" : "min(600px, 100%)" }}
      >
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-titles">
              <div className="dm-kicker">Alte Kundenakte</div>
              <h2 id="zuweisen-titel">
                {vorgaenge ? "Vorgänge aus dieser Akte" : anlage ? "Neuen Kontakt anlegen und zuweisen" : "Ordner einem Kontakt zuweisen"}
              </h2>
            </div>
            <button type="button" className="dm-x" onClick={vorgaenge ? vorgaengeFertig : onClose} aria-label="Schließen" disabled={busy}>
              ×
            </button>
          </div>
        </div>
        {vorgaenge ? (
          <div className="dm-body" style={{ display: "grid", gap: 14 }}>
            <div className="ak-muted" style={{ whiteSpace: "normal" }}>
              Ordner <strong>{folder.folderName}</strong> ist zugewiesen.
            </div>
            <VorgaengeEntscheiden folderId={folder.id} eintraege={vorgaenge} onFertig={vorgaengeFertig} onSpaeter={vorgaengeFertig} />
          </div>
        ) : (
        <div className="dm-body" style={{ display: "grid", gap: 14 }}>
          <div className="ak-pop-hinweis">
            Ordner <strong>{folder.folderName}</strong>
            <br />
            Die Dokumente werden aus dem Speicher gebucht, in der Regel binnen zwei Minuten. Bei einer Firma
            bekommt der ganze Unternehmensverbund die Akte.
          </div>

          {anlage ? (
            <NeuerKontaktAnlage
              felder={anlage}
              set={setFeld}
              typ={typ}
              onTyp={setTyp}
              onAbbrechen={() => {
                setAnlage(null);
                setError(null);
              }}
              onVollstaendig={vollstaendigeAnlage}
              // Ist der Kontakt schon angelegt (nur die Zuweisung scheiterte),
              // entstünde über die Vollanlage ein zweiter.
              onVollanlageAngelegt={angelegt ? undefined : (neu) => void submit(neu)}
              gesperrt={busy}
            />
          ) : (
            <>
              {candidates.length > 0 && (
                <div>
                  <div className="ak-muted" style={{ marginBottom: 6 }}>Vorschläge aus dem Namensabgleich:</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {candidates.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className={"ak-rowbtn" + (kunde?.id === c.id ? " primary" : "")}
                        onClick={() => setKunde(c)}
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <KundePicker value={kunde} onChange={setKunde} label="Kontakt" onNeu={anlageStarten} />
            </>
          )}

          {bisher.length > 0 && !anlage && (
            <div className="ak-muted" style={{ whiteSpace: "normal" }}>
              Bisher zugewiesen an {bisher.join(", ")}. Nach dem Speichern gehört die Akte dem gewählten Kontakt
              und seinem Verbund; bereits gebuchte Dokumente bleiben erhalten.
            </div>
          )}
          {error && <div className="ak-fehler" role="alert">{error}</div>}
        </div>
        )}
        {!vorgaenge && (
          <div className="dm-foot" style={{ alignItems: "center" }}>
            {anlage && anlageFehlt && <span className="ak-muted" style={{ marginRight: "auto" }}>{anlageFehlt}</span>}
            <button type="button" className="tbtn" onClick={onClose} disabled={busy}>
              Abbrechen
            </button>
            <button type="button" className="tbtn primary" onClick={() => void submit()} disabled={!abschicken || busy}>
              {busy ? "Bitte warten …" : anlage ? "Anlegen & zuweisen" : "Zuweisen"}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ZuweisenPopup;
